import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CalendarDays, Edit3, MapPin, Save, ShieldCheck, User, UserCog, X } from "lucide-react";
import { IMaskInput } from "react-imask";
import { buildUploadUrl } from "../utils/runtimeConfig";
import PainelDatePicker from "../components/PainelDatePicker";
import FilaZeroSelect from "../components/FilaZeroSelect";

const estadoOptions = [
  ["AC", "Acre"], ["AL", "Alagoas"], ["AP", "Amapá"], ["AM", "Amazonas"],
  ["BA", "Bahia"], ["CE", "Ceará"], ["DF", "Distrito Federal"],
  ["ES", "Espírito Santo"], ["GO", "Goiás"], ["MA", "Maranhão"],
  ["MT", "Mato Grosso"], ["MS", "Mato Grosso do Sul"], ["MG", "Minas Gerais"],
  ["PA", "Pará"], ["PB", "Paraíba"], ["PR", "Paraná"], ["PE", "Pernambuco"],
  ["PI", "Piauí"], ["RJ", "Rio de Janeiro"], ["RN", "Rio Grande do Norte"],
  ["RS", "Rio Grande do Sul"], ["RO", "Rondônia"], ["RR", "Roraima"],
  ["SC", "Santa Catarina"], ["SP", "São Paulo"], ["SE", "Sergipe"],
  ["TO", "Tocantins"],
].map(([value, label]) => ({ value, label: `${value} — ${label}` }));

const emptyForm = {
  nome: "",
  email: "",
  telefone: "",
  tipo: "",
  data_nascimento: "",
  genero: "",
  endereco_rua: "",
  endereco_numero: "",
  endereco_complemento: "",
  endereco_bairro: "",
  endereco_cidade: "",
  endereco_estado: "",
  endereco_cep: "",
  foto: "",
};

const tipoInfoMap = {
  super_admin: { label: "Administrador geral", className: "role-admin" },
  admin: { label: "Administrador", className: "role-admin" },
  profissional: { label: "Profissional", className: "role-professional" },
  cliente: { label: "Cliente", className: "role-client" },
};

const getFotoSrc = (foto) => {
  if (!foto || typeof foto !== "string") return "";
  if (foto.startsWith("data:") || foto.startsWith("blob:") || foto.startsWith("http")) return foto;
  return buildUploadUrl(foto);
};

const getTipoInfo = (tipo) =>
  tipoInfoMap[String(tipo || "").toLowerCase()] || { label: "Tipo não informado", className: "role-muted" };

const normalizeDate = (value) => {
  if (!value || String(value).toLowerCase() === "invalid date") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const formatarData = (value) => {
  const normalized = normalizeDate(value);
  if (!normalized) return "Não informado";
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

const formatarGenero = (genero) => {
  const normalized = String(genero || "").toLowerCase();
  if (normalized === "masculino") return "Masculino";
  if (normalized === "feminino") return "Feminino";
  return "Não informado";
};

const formatarEndereco = (usuario) => {
  const linha1 = [usuario?.endereco_rua, usuario?.endereco_numero].filter(Boolean).join(", ");
  const linha2 = [usuario?.endereco_bairro, usuario?.endereco_cidade].filter(Boolean).join(" - ");
  const linha3 = [usuario?.endereco_estado, usuario?.endereco_cep].filter(Boolean).join(" - ");
  const linhas = [linha1, usuario?.endereco_complemento, linha2, linha3].filter(Boolean);
  return linhas.length ? linhas : ["Não informado"];
};

const Perfil = () => {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    carregarUsuario();
  }, []);

  const carregarUsuario = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/usuarios/me");
      setUsuario(data);
      setForm({
        ...emptyForm,
        nome: data.nome || "",
        email: data.email || "",
        telefone: data.telefone || "",
        tipo: data.tipo || "",
        data_nascimento: normalizeDate(data.data_nascimento),
        genero: data.genero || "",
        endereco_rua: data.endereco_rua || "",
        endereco_numero: data.endereco_numero || "",
        endereco_complemento: data.endereco_complemento || "",
        endereco_bairro: data.endereco_bairro || "",
        endereco_cidade: data.endereco_cidade || "",
        endereco_estado: data.endereco_estado || "",
        endereco_cep: data.endereco_cep || "",
        foto: data.foto || "",
      });
    } catch (err) {
      console.error("Erro ao carregar dados do usuário:", err);
      toast.error("Erro ao carregar dados do usuário!");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const salvarPerfil = async () => {
    try {
      const payload = { ...form };
      if (!payload.data_nascimento || !/^\d{4}-\d{2}-\d{2}$/.test(payload.data_nascimento)) {
        payload.data_nascimento = null;
      }

      const formData = new FormData();
      [
        "nome",
        "email",
        "telefone",
        "data_nascimento",
        "genero",
        "endereco_rua",
        "endereco_numero",
        "endereco_complemento",
        "endereco_bairro",
        "endereco_cidade",
        "endereco_estado",
        "endereco_cep",
      ].forEach((campo) => {
        const valor = payload[campo];
        if (valor === null || valor === undefined || valor === "") return;
        formData.append(campo, valor);
      });

      if (payload.fotoFile instanceof File) {
        formData.append("foto", payload.fotoFile);
      } else if (payload.fotoRemove) {
        formData.append("foto_remove", "true");
      }

      await api.put("/usuarios/me", formData);
      toast.success("Perfil atualizado com sucesso!");
      setTimeout(() => {
        setEditMode(false);
        carregarUsuario();
      }, 600);
    } catch (err) {
      console.error("Erro ao atualizar perfil:", err);
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.mensagem || "Este e-mail já está em uso.");
      } else if (!err.response || err.code === "ERR_NETWORK" || /proxy/i.test(String(err.response?.data || ""))) {
        toast.error("Não foi possível conectar à API. Verifique se o backend está rodando e tente de novo.");
      } else {
        toast.error(err.response?.data?.mensagem || "Erro ao atualizar perfil!");
      }
    }
  };

  const buscarEnderecoPorCep = async (cep) => {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep.replace(/\D/g, "")}/json/`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.erro ? null : data;
    } catch {
      return null;
    }
  };

  const handleCepBlur = async (e) => {
    const cep = e.target.value;
    if (cep && cep.replace(/\D/g, "").length === 8) {
      const endereco = await buscarEnderecoPorCep(cep);
      if (endereco) {
        setForm((prev) => ({
          ...prev,
          endereco_rua: endereco.logradouro || "",
          endereco_bairro: endereco.bairro || "",
          endereco_cidade: endereco.localidade || "",
          endereco_estado: endereco.uf || "",
        }));
      }
    }
  };

  if (loading) return <p>Carregando...</p>;

  const tipoInfo = getTipoInfo(usuario?.tipo || form.tipo);
  const enderecoLinhas = formatarEndereco(usuario);

  return (
    <motion.div
      className="container fade-in profile-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <ToastContainer position="top-right" />

      <h2 className="profile-page-title">
        <UserCog /> Meu Perfil
      </h2>

      <motion.div
        className="card-block profile-card"
        initial={{ opacity: 0, scale: 0.98, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {!editMode ? (
          <>
            <div className="profile-summary">
              <div className="profile-avatar">
                {usuario?.foto ? (
                  <img src={getFotoSrc(usuario.foto)} alt="Foto do usuário" />
                ) : (
                  <User size={70} />
                )}
              </div>
              <div className="profile-summary-meta">
                <span className="profile-kicker">Dados da conta</span>
                <div className="profile-name">{usuario?.nome || "Usuário"}</div>
                <div className="profile-contact-line">{usuario?.email || "E-mail não informado"}</div>
                <div className="profile-contact-line">{usuario?.telefone || "Telefone não informado"}</div>
                <span className={`user-role-badge ${tipoInfo.className}`}>
                  <ShieldCheck size={14} />
                  {tipoInfo.label}
                </span>
              </div>
            </div>

            <div className="profile-info-grid">
              <div className="profile-info-item">
                <strong>Data de nascimento</strong>
                <span className="profile-info-value">
                  <CalendarDays size={16} />
                  {formatarData(usuario?.data_nascimento)}
                </span>
              </div>
              <div className="profile-info-item">
                <strong>Gênero</strong>
                {formatarGenero(usuario?.genero)}
              </div>
            </div>

            <div className="profile-info-item profile-info-item--wide">
              <strong>
                <MapPin size={14} />
                Endereço
              </strong>
              {enderecoLinhas.map((linha) => (
                <span key={linha} className="profile-address-line">
                  {linha}
                </span>
              ))}
            </div>

            <button className="btn profile-primary-action" onClick={() => setEditMode(true)}>
              <Edit3 size={18} />
              Editar perfil
            </button>
          </>
        ) : (
          <form className="profile-form">
            <section className="profile-section profile-form-full">
              <div className="profile-section__header">
                <h3>Foto do perfil</h3>
                <p>Use uma imagem clara para facilitar a identificação no painel.</p>
              </div>
              <div className="profile-photo-editor">
                <div className="profile-avatar">
                  {form.foto ? (
                    <img src={getFotoSrc(form.foto)} alt="Foto do usuário" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <User size={70} />
                  )}
                </div>
                <div className="profile-photo-actions">
                  <label htmlFor="foto-input" className="profile-photo-button">
                    Selecionar foto
                  </label>
                  <input
                    id="foto-input"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.type.startsWith("image/")) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setForm((prev) => ({ ...prev, foto: event.target.result, fotoFile: file, fotoRemove: false }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {form.foto && (
                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, foto: "", fotoFile: null, fotoRemove: true }))}
                    >
                      Remover foto
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="profile-section profile-form-full">
              <div className="profile-section__header">
                <h3>Dados pessoais</h3>
                <p>Essas informações aparecem nos registros administrativos do painel.</p>
              </div>
              <div className="profile-form-grid">
                <div>
                  <label className="field-label">Nome</label>
                  <input className="field" name="nome" value={form.nome} onChange={handleChange} type="text" autoFocus />
                </div>
                <div>
                  <label className="field-label">E-mail</label>
                  <input className="field" name="email" value={form.email} onChange={handleChange} type="email" />
                </div>
                <div>
                  <label className="field-label">Telefone</label>
                  <IMaskInput
                    className="field"
                    name="telefone"
                    value={form.telefone}
                    onAccept={(value) => handleChange({ target: { name: "telefone", value } })}
                    mask="(00) 00000-0000"
                    placeholder="(99) 99999-9999"
                  />
                </div>
                <div>
                  <label className="field-label">Tipo</label>
                  <div className="profile-type-readonly">
                    <span className={`user-role-badge ${getTipoInfo(form.tipo).className}`}>
                      {getTipoInfo(form.tipo).label}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="field-label">Data de nascimento</label>
                  <PainelDatePicker
                    name="data_nascimento"
                    value={form.data_nascimento || ""}
                    onChange={handleChange}
                    placeholder="Selecione a data"
                  />
                </div>
                <div className="profile-form-full">
                  <label className="field-label">Gênero</label>
                  <div className="profile-radio-row">
                    <label>
                      <input type="radio" name="genero" value="masculino" checked={form.genero === "masculino"} onChange={handleChange} />
                      Masculino
                    </label>
                    <label>
                      <input type="radio" name="genero" value="feminino" checked={form.genero === "feminino"} onChange={handleChange} />
                      Feminino
                    </label>
                  </div>
                </div>
              </div>
            </section>

            <section className="profile-section profile-form-full">
              <div className="profile-section__header">
                <h3>Endereço</h3>
                <p>O CEP preenche automaticamente rua, bairro, cidade e estado quando encontrado.</p>
              </div>
              <div className="profile-form-grid">
                <div>
                  <label className="field-label">CEP</label>
                  <IMaskInput
                    className="field"
                    name="endereco_cep"
                    value={form.endereco_cep}
                    onAccept={(value) => handleChange({ target: { name: "endereco_cep", value } })}
                    mask="00000-000"
                    placeholder="00000-000"
                    onBlur={handleCepBlur}
                  />
                </div>
                <div>
                  <label className="field-label">Rua</label>
                  <input className="field" name="endereco_rua" value={form.endereco_rua} onChange={handleChange} type="text" />
                </div>
                <div>
                  <label className="field-label">Número</label>
                  <input className="field" name="endereco_numero" value={form.endereco_numero} onChange={handleChange} type="text" />
                </div>
                <div>
                  <label className="field-label">Complemento</label>
                  <input className="field" name="endereco_complemento" value={form.endereco_complemento} onChange={handleChange} type="text" />
                </div>
                <div>
                  <label className="field-label">Bairro</label>
                  <input className="field" name="endereco_bairro" value={form.endereco_bairro} onChange={handleChange} type="text" />
                </div>
                <div>
                  <label className="field-label">Cidade</label>
                  <input className="field" name="endereco_cidade" value={form.endereco_cidade} onChange={handleChange} type="text" />
                </div>
                <div>
                  <label className="field-label">Estado</label>
                  <FilaZeroSelect
                    name="endereco_estado"
                    value={form.endereco_estado}
                    options={estadoOptions}
                    onChange={handleChange}
                    placeholder="Selecione o estado"
                  />
                </div>
              </div>
            </section>

            <div className="profile-actions">
              <button className="btn" onClick={salvarPerfil} type="button">
                <Save size={18} />
                Salvar
              </button>
              <button className="btn-secondary" onClick={() => setEditMode(false)} type="button">
                <X size={18} />
                Cancelar
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Perfil;
